import {Injectable} from '@angular/core';
import * as Moment from 'moment';
import * as Sentry from '@sentry/angular-ivy';
import {TransientPatient} from '../models/order-entry/patient';

@Injectable()
export class RecentPatientService {
  constructor() {
    this.verifyRecentPatients(this.getRecentPatients());
  }

  public static isLocalStorageAvailable(): boolean {
    return typeof localStorage !== 'undefined';

  }

  public static isWebWorkersAvailable(): boolean {
    return typeof Worker !== 'undefined';

  }

  public verifyRecentPatients(patients: any[]) {
    const recent = patients;
    const patientsIds = recent
      .filter(patient => {
        const deleteDate = Moment(patient.shouldBeDeletedDate);
        const currentDate = Moment();
        return deleteDate.isBefore(currentDate);
      })
      .map(p => p.id);

    if (patientsIds.length > 0) {
      try {
        patientsIds.forEach(id =>
          recent.splice(
            recent.findIndex(p => p.id === id),
            1,
          ),
        );
      } catch (e) {
        Sentry.captureException(e);
      }
      this.save(patients);
    }
  }

  public getRecentPatients(): TransientPatient[] {
    if (!RecentPatientService.isLocalStorageAvailable()) {
      return [];
    }
    const recentPatients = localStorage.getItem('recentPatients');
    let result: TransientPatient[];
    if (recentPatients) {
      result = JSON.parse(recentPatients);
    } else {
      result = [];
    }

    return result;
  }

  public addRecentPatient(patient: TransientPatient): void {
    if (!RecentPatientService.isLocalStorageAvailable()) {
      return;
    }
    if (patient === undefined || patient === null) {
      return;
    }
    if (this.exists(patient.id)) {
      return;
    }

    const patients = this.getRecentPatients();
    patient.recentDate = Moment().toDate();
    patient.shouldBeDeletedDate = Moment().add('12', 'hours').toDate();
    patients.push(patient);
    this.save(patients);
  }

  public updatePatient(patient: TransientPatient) {
    if (!RecentPatientService.isLocalStorageAvailable()) {
      return;
    }
    if (patient === undefined || patient === null) {
      return;
    }

    const patients = this.getRecentPatients();
    const index = patients.findIndex((p) => patient.id === p.id);
    if (index === -1) {
      return;
    }

    patients.splice(index, 1);
    patient.recentDate = Moment().toDate();
    patient.shouldBeDeletedDate = Moment().add('12', 'hours').toDate();
    patients.push(patient);
    this.save(patients);
  }

  private save(patients: any[]): void {
    const patientsString = JSON.stringify(patients);
    localStorage.setItem('recentPatients', patientsString);
  }

  public async hasAny(): Promise<boolean> {
    if (!RecentPatientService.isLocalStorageAvailable()) {
      return new Promise<boolean>(() => false);
    }
    return this.getRecentPatients().length > 0;
  }

  public exists(patientId: number): boolean {
    if (!RecentPatientService.isLocalStorageAvailable()) {
      return false;
    }
    const patients = this.getRecentPatients();
    return patients.findIndex((patient: TransientPatient) => patient.id === patientId) !== -1;
  }
}
